import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, Plus, Sparkles, Scale, TrendingUp, TrendingDown,
  Landmark, Coins, Banknote, ChevronRight } from "lucide-react";

const TYPE_META = {
  asset:        { label: "Assets",        icon: Landmark,     tone: "border-sky-500/40 text-sky-300 bg-sky-500/5" },
  liability:    { label: "Liabilities",   icon: Scale,        tone: "border-rose-500/40 text-rose-300 bg-rose-500/5" },
  equity:       { label: "Equity",        icon: Coins,        tone: "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/5" },
  revenue:      { label: "Revenue",       icon: TrendingUp,   tone: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
  expense:      { label: "Expenses",      icon: TrendingDown, tone: "border-amber-500/40 text-amber-300 bg-amber-500/5" },
  distribution: { label: "Distributions", icon: Banknote,     tone: "border-indigo-500/40 text-indigo-300 bg-indigo-500/5" },
};
const TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense", "distribution"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ChartOfAccountsPage() {
  const { currentProperty, propertyId } = useProperty();
  const pid = currentProperty?.id || propertyId;
  const [data, setData] = useState(null);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [activeType, setActiveType] = useState("all");

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/admin/coa", { params: { property_id: pid } }),
        api.get("/admin/coa/rollup", { params: { property_id: pid, year } }).catch(() => ({ data: null })),
      ]);
      setData(c.data);
      setRollup(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setLoading(false);
  }, [pid, year]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!pid) return toast.error("Pick a property first");
    try {
      const { data: r } = await api.post("/admin/coa/seed-defaults", { property_id: pid });
      toast.success(`${r.inserted} accounts added (${r.already_present} already existed)`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const grouped = data?.grouped || {};
  const activityByCode = useMemo(() => {
    if (!rollup) return {};
    const m = {};
    (rollup.rows || []).forEach(r => { m[r.code] = r; });
    return m;
  }, [rollup]);

  const filteredTypes = activeType === "all" ? TYPE_ORDER : [activeType];
  const empty = data && (data.total || 0) === 0;

  return (
    <div className="space-y-6" data-testid="coa-page">
      <PageHeader
        eyebrow="Financials"
        title="Chart of Accounts"
        subtitle="The canonical map of every GL account. Numeric codes drive P&L, Balance Sheet, and trial-balance reports."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
                   className="w-24 bg-white/[0.02] border-white/10" data-testid="coa-year" />
            <Button variant="outline" className="border-white/10" onClick={load} data-testid="coa-refresh">Refresh</Button>
            <Button variant="outline" className="border-white/10 gap-2"
                    onClick={seed} data-testid="coa-seed-btn">
              <Sparkles className="h-4 w-4" /> Seed standard chart
            </Button>
            <AccountDialog propertyId={pid} onSaved={load} />
          </div>
        }
      />

      {/* Trial balance strip */}
      {rollup && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="coa-trial-balance">
          <Kpi label="Total accounts" value={data?.total || 0} sub={`${TYPE_ORDER.length} categories`} />
          <Kpi label={`Total debits ${year}`} value={money(rollup.totals?.debits)} tone="bad" />
          <Kpi label={`Total credits ${year}`} value={money(rollup.totals?.credits)} tone="good" />
          <Kpi label="Trial balance"
               value={rollup.totals?.in_balance ? "In balance" : "Out of balance"}
               tone={rollup.totals?.in_balance ? "good" : "bad"}
               sub={`Δ ${money(Math.abs((rollup.totals?.debits || 0) - (rollup.totals?.credits || 0)))}`} />
        </div>
      )}

      {/* Type filter tabs */}
      <div className="flex flex-wrap gap-2 -mt-2">
        <Chip active={activeType === "all"} onClick={() => setActiveType("all")} testId="coa-tab-all">
          All ({data?.total || 0})
        </Chip>
        {TYPE_ORDER.map(t => (
          <Chip key={t} active={activeType === t} onClick={() => setActiveType(t)} testId={`coa-tab-${t}`}>
            {TYPE_META[t].label} ({data?.counts?.[t] || 0})
          </Chip>
        ))}
      </div>

      {empty && (
        <Card className="border-white/10 bg-card p-10 text-center" data-testid="coa-empty">
          <BookOpen className="h-8 w-8 mx-auto mb-3 text-zinc-500" />
          <div className="text-lg font-semibold">No chart of accounts yet</div>
          <div className="text-sm text-zinc-400 mt-2 mb-4">
            Seed the standard 44-account PM/HOA chart to get started, then customize per property.
          </div>
          <Button onClick={seed} className="bg-orange-500 hover:bg-orange-600" data-testid="coa-seed-empty">
            <Sparkles className="h-4 w-4 mr-2" /> Seed standard chart
          </Button>
        </Card>
      )}

      {loading && !data && <div className="text-sm text-zinc-500">Loading…</div>}

      {filteredTypes.map(t => {
        const rows = grouped[t] || [];
        if (rows.length === 0) return null;
        const M = TYPE_META[t];
        const Icon = M.icon;
        return (
          <Card key={t} className="border-white/10 bg-card p-0 overflow-hidden" data-testid={`coa-section-${t}`}>
            <div className={`flex items-center justify-between px-5 py-3 border-b border-white/10 ${M.tone.split(" ").slice(-1)[0]}`}>
              <div className="flex items-center gap-3">
                <Icon className={`h-4 w-4 ${M.tone.split(" ")[1]}`} />
                <div className="font-semibold">{M.label}</div>
                <Badge variant="outline" className={M.tone}>{rows.length} accounts</Badge>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2 w-20">Code</th>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-right px-4 py-2 hidden sm:table-cell">Debits {year}</th>
                    <th className="text-right px-4 py-2 hidden sm:table-cell">Credits {year}</th>
                    <th className="text-right px-4 py-2">Activity</th>
                    <th className="text-right px-4 py-2 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {rows.map(row => {
                    const act = activityByCode[row.code];
                    const activity = act?.activity || 0;
                    return (
                      <tr key={row.id} className="hover:bg-white/[0.02]" data-testid={`coa-row-${row.code}`}>
                        <td className="px-4 py-2 font-mono text-zinc-400">{row.code}</td>
                        <td className="px-4 py-2">
                          {row.name}
                          {row.is_system && <Badge variant="outline" className="ml-2 border-white/10 text-[10px]">system</Badge>}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-zinc-400 hidden sm:table-cell">
                          {act ? money(act.debits) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-zinc-400 hidden sm:table-cell">
                          {act ? money(act.credits) : "—"}
                        </td>
                        <td className={"px-4 py-2 text-right font-mono " + (activity >= 0 ? "text-emerald-300" : "text-rose-300")}>
                          {act ? money(activity) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <AccountDialog propertyId={pid} account={row} onSaved={load} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "default" }) {
  const toneClasses = {
    default: "border-white/10 bg-card",
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    bad: "border-rose-500/20 bg-rose-500/[0.03]",
  };
  return (
    <Card className={`p-4 ${toneClasses[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold font-display mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
        (active ? "border-orange-400/60 bg-orange-500/10 text-orange-200"
                : "border-white/10 bg-white/[0.02] text-zinc-400 hover:text-zinc-200 hover:border-white/20")}
    >
      {children}
    </button>
  );
}

function AccountDialog({ propertyId, account, onSaved }) {
  const editing = !!account;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", type: "expense", description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(account
        ? { code: account.code, name: account.name, type: account.type,
             description: account.description || "", active: account.active }
        : { code: "", name: "", type: "expense", description: "" });
    }
  }, [open, account]);

  const save = async () => {
    if (!editing && !propertyId) return toast.error("Pick a property first");
    if (!form.code?.trim() || !form.name?.trim()) return toast.error("Code and name are required");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/coa/${account.id}`, {
          name: form.name, description: form.description,
          active: form.active !== false,
        });
        toast.success("Account updated");
      } else {
        await api.post("/admin/coa", {
          property_id: propertyId,
          code: form.code.trim(), name: form.name.trim(),
          type: form.type, description: form.description,
        });
        toast.success("Account created");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const remove = async () => {
    if (!editing) return;
    if (account.is_system) return toast.error("System accounts can only be archived, not deleted");
    if (!window.confirm("Archive this account? It stays in reports but new entries can't post to it.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/coa/${account.id}`);
      toast.success("Account archived");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button size="sm" variant="ghost" className="text-xs" data-testid={`coa-edit-${account.code}`}>Edit</Button>
          : <Button className="bg-orange-500 hover:bg-orange-600" data-testid="coa-new-btn">
              <Plus className="h-4 w-4 mr-2" /> New account
            </Button>}
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="coa-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${account.code} · ${account.name}` : "New GL account"}</DialogTitle>
          <DialogDescription className="text-xs">
            Codes: 1xxx assets · 2xxx liabilities · 3xxx equity · 4xxx revenue · 5xxx expenses · 6xxx distributions
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Code</Label>
              <Input value={form.code} disabled={editing}
                     onChange={(e) => setForm({ ...form, code: e.target.value })}
                     placeholder="5100" data-testid="coa-form-code" />
            </div>
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Utilities — Water/Sewer" data-testid="coa-form-name" />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={editing}>
              <SelectTrigger className="bg-white/[0.02] border-white/10" data-testid="coa-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_ORDER.map(t => <SelectItem key={t} value={t}>{TYPE_META[t].label} ({t})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Water and sewer service billed to the property" data-testid="coa-form-desc" />
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {editing && !account.is_system && (
            <Button variant="ghost" className="text-rose-400" onClick={remove} data-testid="coa-form-archive">
              Archive
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="coa-form-save">
            {busy ? "Saving…" : editing ? "Save changes" : "Create account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
